import React, { useState, useRef, useEffect, useCallback } from 'react';
import { C } from './AnalyticsTokens';
import { STATUS_CONFIG, STATUS_OPTIONEN, NOTE_TAG_COLORS } from './AnalyticsConstants';
import { 
  parseNotizen, 
  serializeNotizen, 
  newNote, 
  formatNoteDate, 
  formatDate, 
  getNoteTag, 
  formatGehalt 
} from './AnalyticsUtils';
import { 
  AbgeschicktIcon, 
  StarSVG, 
  EditSVG, 
  ChevronSVG, 
  PersonSVG, 
  CalendarSVG, 
  EuroSVG, 
  DotsMenuSVG 
} from './AnalyticsIcons';
import { MiniCalendar } from './AnalyticsComponents';

export function StelleCard({ stelle, expanded, onToggle, save, markierteIds, onMarkierenToggle, onBearbeiten, mobileSheet = false }) {
  const [notizen, setNotizen]           = useState(() => parseNotizen(stelle.notizen));
  const [notizDraft, setNotizDraft]     = useState('');
  const [notizenOpen, setNotizenOpen]   = useState(false);
  const [statusDdOpen, setStatusDdOpen] = useState(false);
  const [localStatus, setLocalStatus]   = useState(stelle.bewerbungs_status || '');
  const statusRef    = useRef(null);
  const notizInputRef = useRef(null);

  useEffect(() => {
    setNotizen(parseNotizen(stelle.notizen));
  }, [stelle.id, stelle.notizen]);

  useEffect(() => {
    setNotizenOpen(false);
  }, [stelle.id]);

  useEffect(() => {
    setLocalStatus(stelle.bewerbungs_status || '');
  }, [stelle.id, stelle.bewerbungs_status]);

  useEffect(() => {
    if (!statusDdOpen) return;
    function h(e) { if (statusRef.current && !statusRef.current.contains(e.target)) setStatusDdOpen(false); }
    document.addEventListener('mousedown', h);
    document.addEventListener('touchstart', h);
    return () => { document.removeEventListener('mousedown', h); document.removeEventListener('touchstart', h); };
  }, [statusDdOpen]);

  const st          = localStatus;
  const cfg         = STATUS_CONFIG[st] ?? STATUS_CONFIG[''];
  const name        = stelle.empfaenger_unternehmen || stelle.name || 'Unbenannt';
  const dateStr     = formatDate(stelle.erstellt_am);
  const istMarkiert = markierteIds?.has(stelle.id);
  const notizenCnt  = notizen.length;

  const persistNotizen = useCallback((updated) => {
    setNotizen(updated);
    save?.(stelle.id, { notizen: serializeNotizen(updated) });
  }, [stelle.id, save]);

  const addNotiz = useCallback(() => {
    if (!notizDraft.trim()) return;
    persistNotizen([...notizen, newNote(notizDraft.trim())]);
    setNotizDraft('');
  }, [notizDraft, notizen, persistNotizen]);

  const changeStatus = useCallback((value) => {
    setLocalStatus(value);
    save?.(stelle.id, { bewerbungs_status: value });
    setStatusDdOpen(false);
  }, [stelle.id, save]);

  const openNotizenInput = useCallback(() => {
    setNotizenOpen(true);
    setTimeout(() => notizInputRef.current?.focus(), 180);
  }, []);

  const inputStyle = {
    width: '100%', padding: '9px 12px',
    border: `1.5px solid ${C.borderMid}`, borderRadius: 10,
    fontSize: 14, fontFamily: C.body, color: C.text,
    background: C.bg, outline: 'none',
    WebkitAppearance: 'none', boxSizing: 'border-box',
  };
  const labelStyle = {
    fontSize: 10, fontWeight: 700, letterSpacing: '0.13em',
    textTransform: 'uppercase', color: C.textDim,
    marginBottom: 7, display: 'flex', alignItems: 'center', gap: 5,
  };
  const onFocusInput = e => {
    e.target.style.borderColor = C.amber;
    e.target.style.boxShadow = '0 0 0 3px rgba(255,188,0,0.15)';
  };
  const onBlurReset = e => {
    e.target.style.borderColor = C.borderMid;
    e.target.style.boxShadow = 'none';
  };

  return (
    <div style={{
      background: C.surface,
      borderRadius: mobileSheet ? 22 : (expanded ? 20 : 16),
      border: mobileSheet ? 'none' : `1px solid ${expanded ? C.borderMid : C.border}`,
      boxShadow: mobileSheet ? 'none' : (expanded ? C.shadowCard : C.shadow),
      overflow: 'hidden',
      minHeight: mobileSheet ? '100%' : undefined,
      flexShrink: mobileSheet ? 0 : undefined,
      transition: 'border-radius 0.25s ease, box-shadow 0.25s ease',
    }}>

      <div onClick={onToggle} style={{
        display: 'flex', alignItems: 'center', padding: mobileSheet && expanded ? '25px 18px 15px' : '14px 18px', gap: 12,
        background: expanded ? C.teal : C.surface,
        cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
        transition: 'background 0.25s ease',
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 15, fontWeight: 700,
            color: expanded ? '#fff' : C.text,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            transition: 'color 0.25s ease',
          }}>{name}</div>
          {dateStr && <div style={{
            fontSize: 12,
            color: expanded ? 'rgba(255,255,255,0.6)' : C.textMuted,
            marginTop: 2, transition: 'color 0.25s ease',
          }}>{dateStr}</div>}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexShrink: 0 }}>
          {!expanded && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 5,
              background: cfg.bg, color: cfg.color, border: `1.5px solid ${cfg.border}`,
              fontSize: 11, fontWeight: 700, padding: '5px 12px', borderRadius: 999,
              whiteSpace: 'nowrap',
            }}>
              <AbgeschicktIcon color={cfg.color} size={9} />
              {cfg.label}
            </div>
          )}
          {expanded && (
            <button
              onClick={e => { e.stopPropagation(); onBearbeiten?.(stelle); }}
              style={{
                width: 33, height: 33, borderRadius: 999,
                background: 'rgba(255,255,255,0.13)', border: '1px solid rgba(255,255,255,0.22)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
              }}>
              <DotsMenuSVG size={14} color="rgba(255,255,255,0.75)" />
            </button>
          )}
          <div style={{ transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.25s ease', display: 'flex' }}>
            <ChevronSVG up={false} size={14} opacity={expanded ? 1 : 0.32} color={expanded ? 'rgba(255,255,255,0.75)' : undefined} />
          </div>
        </div>
      </div>

      <div style={{
        display: mobileSheet ? 'block' : 'grid',
        gridTemplateRows: expanded ? '1fr' : '0fr',
        transition: mobileSheet ? 'none' : 'grid-template-rows 0.28s ease',
      }}>
        <div style={{ overflow: mobileSheet ? 'visible' : 'hidden' }}>

      <div style={{ padding: '16px 18px 14px' }} ref={statusRef}>
        <div style={{
          fontSize: 10, fontWeight: 700, letterSpacing: '0.13em',
          textTransform: 'uppercase', color: C.textDim, marginBottom: 9,
        }}>
          Bewerbungs-Status
        </div>
        <div style={{ position: 'relative' }}>
          <button
            onClick={e => { e.stopPropagation(); setStatusDdOpen(v => !v); }}
            style={{
              width: '100%', display: 'flex', alignItems: 'center', gap: 8,
              padding: '11px 16px',
              background: cfg.bg, border: `1.5px solid ${cfg.border}`,
              borderRadius: 999, color: cfg.color,
              fontSize: 13, fontWeight: 700, cursor: 'pointer',
              fontFamily: C.body, WebkitTapHighlightColor: 'transparent',
            }}>
            <AbgeschicktIcon color={cfg.color} size={12} />
            <span style={{ flex: 1, textAlign: 'left' }}>{cfg.label}</span>
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ opacity: 0.65, flexShrink: 0 }}>
              <path d="M3 4.5l3 3 3-3" stroke={cfg.color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>

          {statusDdOpen && (
            <div style={{
              position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0,
              background: C.surface, border: `1px solid ${C.border}`,
              borderRadius: 14, boxShadow: '0 10px 36px rgba(0,0,0,0.14), 0 2px 8px rgba(0,0,0,0.07)',
              zIndex: 400, overflow: 'hidden',
            }}>
              {STATUS_OPTIONEN.map(opt => {
                const oCfg = STATUS_CONFIG[opt.value] ?? STATUS_CONFIG[''];
                const isActive = st === opt.value;
                return (
                  <button key={opt.value} onClick={() => changeStatus(opt.value)} style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    width: '100%', padding: '11px 16px', border: 'none',
                    background: isActive ? C.tealMid : 'transparent',
                    cursor: 'pointer', fontFamily: C.body, WebkitTapHighlightColor: 'transparent',
                  }}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: oCfg.color, flexShrink: 0 }} />
                    <span style={{ fontSize: 13, fontWeight: isActive ? 600 : 400, color: isActive ? C.teal : C.text }}>
                      {opt.label}
                    </span>
                    {isActive && (
                      <svg width="13" height="13" viewBox="0 0 13 13" fill="none" style={{ marginLeft: 'auto' }}>
                        <path d="M2 6.5l3.5 3.5L11 3" stroke={C.teal} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div style={{ borderTop: `1px solid ${C.border}` }}>

        <div style={{ padding: '12px 18px', borderBottom: `1px solid ${C.border}` }}>
          <div style={labelStyle}><PersonSVG size={12} /> Ansprechpartner</div>
          <input
            type="text"
            defaultValue={stelle.empfaenger_ansprechperson || ''}
            placeholder="—"
            style={inputStyle}
            onFocus={onFocusInput}
            onBlur={e => {
              onBlurReset(e);
              if (e.target.value !== (stelle.empfaenger_ansprechperson || ''))
                save?.(stelle.id, { empfaenger_ansprechperson: e.target.value });
            }}
            onKeyDown={e => e.key === 'Enter' && e.target.blur()}
          />
        </div>

        <div style={{ padding: '12px 18px', borderBottom: `1px solid ${C.border}` }}>
          <div style={labelStyle}><CalendarSVG size={12} /> Abgeschickt am</div>
          <MiniCalendar
            value={stelle.abschick_datum || ''}
            onChange={v => {
              const changes = { abschick_datum: v };
              if (v && (!st || st === 'unterlagen_angefertigt')) {
                changes.bewerbungs_status = 'abgeschickt';
                setLocalStatus('abgeschickt');
              }
              save?.(stelle.id, changes);
            }}
            placeholder="TT.MM.JJJJ"
          />
        </div>

        {(st === 'kennenlerngespraech' || !!stelle.interview_datum) && (
          <div style={{ padding: '12px 18px', borderBottom: `1px solid ${C.border}` }}>
            <div style={labelStyle}><CalendarSVG size={12} /> Interview</div>
            <MiniCalendar
              value={stelle.interview_datum || ''}
              onChange={v => save?.(stelle.id, { interview_datum: v })}
              placeholder="Datum wählen"
              style={{ marginBottom: 8 }}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0, opacity: 0.45 }}>
                <circle cx="12" cy="12" r="10" stroke={C.textMuted} strokeWidth="1.7"/>
                <path d="M12 6v6l4 2" stroke={C.textMuted} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <input
                type="time"
                defaultValue={stelle.interview_uhrzeit || ''}
                style={{ ...inputStyle, flex: 1 }}
                onFocus={onFocusInput}
                onBlur={e => {
                  onBlurReset(e);
                  save?.(stelle.id, { interview_uhrzeit: e.target.value });
                }}
              />
            </div>
          </div>
        )}

        <div style={{ padding: '12px 18px' }}>
          <div style={labelStyle}><EuroSVG size={12} /> Gehaltsvorstellung</div>
          <input
            type="text"
            defaultValue={stelle.gehaltsvorstellung || ''}
            placeholder="z.B. 55.000 €"
            style={inputStyle}
            onFocus={onFocusInput}
            onBlur={e => {
              onBlurReset(e);
              const formatted = formatGehalt(e.target.value);
              if (formatted !== e.target.value) e.target.value = formatted;
              if (formatted !== (stelle.gehaltsvorstellung || ''))
                save?.(stelle.id, { gehaltsvorstellung: formatted });
            }}
            onKeyDown={e => e.key === 'Enter' && e.target.blur()}
          />
        </div>

      </div>

      <div style={{ borderTop: `1px solid ${C.border}` }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 18px 10px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: C.textDim }}>
              Notizen
            </span>
            {notizenCnt > 0 && (
              <span style={{ fontSize: 11, fontWeight: 700, color: C.textDim }}>— {notizenCnt}</span>
            )}
          </div>
          <button
            onClick={openNotizenInput}
            style={{
              fontSize: 12, color: C.teal, fontWeight: 600,
              background: 'none', border: 'none', cursor: 'pointer',
              WebkitTapHighlightColor: 'transparent', padding: 0, fontFamily: C.body,
            }}>
            Alle ansehen →
          </button>
        </div>

        <div style={{
          display: 'flex', gap: 9, overflowX: 'auto',
          padding: '0 18px 14px',
          scrollbarWidth: 'none', msOverflowStyle: 'none',
          WebkitOverflowScrolling: 'touch',
        }}>
          {notizen.length === 0 && (
            <div style={{ fontSize: 13, color: C.textMuted, padding: '6px 0' }}>Noch keine Notizen.</div>
          )}
          {notizen.map(n => {
            const noteDate = n.erstellt_am
              ? new Date(n.erstellt_am).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })
              : '';
            const tag     = getNoteTag(n.text);
            const tagCfg  = tag ? NOTE_TAG_COLORS[tag] : null;
            return (
              <div key={n.id} style={{
                flexShrink: 0, width: 148,
                background: C.bg, borderRadius: 14, padding: '10px 12px',
                border: `1px solid ${C.borderMid}`,
              }}>
                {noteDate && (
                  <div style={{ fontSize: 10, fontWeight: 700, color: C.textMuted, marginBottom: tagCfg ? 5 : 6 }}>
                    {noteDate}.
                  </div>
                )}
                {tagCfg && (
                  <div style={{
                    display: 'inline-flex', alignItems: 'center',
                    background: tagCfg.bg, color: tagCfg.color,
                    fontSize: 9, fontWeight: 800, letterSpacing: '0.10em',
                    padding: '2px 7px', borderRadius: 999, marginBottom: 6,
                    textTransform: 'uppercase',
                  }}>
                    {tag}
                  </div>
                )}
                <div style={{
                  fontSize: 12, color: C.text, lineHeight: 1.45,
                  overflow: 'hidden', display: '-webkit-box',
                  WebkitLineClamp: 4, WebkitBoxOrient: 'vertical',
                }}>
                  {n.text}
                </div>
              </div>
            );
          })}
          <div
            onClick={openNotizenInput}
            style={{
              flexShrink: 0, width: 72,
              background: 'transparent', borderRadius: 14, padding: '10px 0',
              border: `1.5px dashed ${C.borderMid}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
              minHeight: 70,
            }}>
            <span style={{ fontSize: 24, color: C.textDim, lineHeight: 1, fontWeight: 300 }}>+</span>
          </div>
        </div>

        {notizenOpen && (
          <div style={{ padding: '4px 18px 14px', display: 'flex', flexDirection: 'column', gap: 8, borderTop: `1px solid ${C.border}` }}>
            {notizen.map(n => (
              <div key={n.id} style={{ background: C.bg, borderRadius: 10, padding: '10px 12px', position: 'relative' }}>
                <div style={{ fontSize: 13, color: C.text, lineHeight: 1.5, paddingRight: 20 }}>{n.text}</div>
                {n.erstellt_am && <div style={{ fontSize: 10, color: C.textMuted, marginTop: 4 }}>{formatNoteDate(n.erstellt_am)}</div>}
                <button onClick={() => persistNotizen(notizen.filter(x => x.id !== n.id))} style={{
                  position: 'absolute', top: 8, right: 8, border: 'none', background: 'transparent',
                  color: C.textMuted, fontSize: 15, cursor: 'pointer', padding: 2, lineHeight: 1,
                  WebkitTapHighlightColor: 'transparent',
                }}>×</button>
              </div>
            ))}
            <div style={{ display: 'flex', gap: 8, marginTop: 2 }}>
              <input
                ref={notizInputRef}
                value={notizDraft}
                onChange={e => setNotizDraft(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addNotiz(); } }}
                placeholder="Notiz hinzufügen…"
                style={{
                  flex: 1, padding: '9px 13px',
                  border: `1.5px solid ${C.borderMid}`, borderRadius: 10,
                  fontSize: 13, fontFamily: C.body, color: C.text,
                  background: C.bg, outline: 'none',
                  WebkitAppearance: 'none',
                }}
              />
              <button onClick={addNotiz} style={{
                padding: '9px 16px', borderRadius: 10,
                background: C.amber, border: 'none', color: '#3A2700',
                fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: C.body,
                WebkitTapHighlightColor: 'transparent',
              }}>+</button>
            </div>
          </div>
        )}
      </div>

      <div style={{ borderTop: `1px solid ${C.border}`, padding: mobileSheet ? '12px 18px calc(env(safe-area-inset-bottom, 0px) + 18px)' : '12px 18px 16px', display: 'flex', gap: 8 }}>
        <button onClick={() => onMarkierenToggle?.(stelle.id)} style={{
          flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
          padding: '9px 8px', border: `1px solid ${C.borderMid}`, borderRadius: 999,
          background: istMarkiert ? C.amberDim : 'transparent',
          color: istMarkiert ? '#7A5000' : C.textMuted,
          fontSize: 12, fontWeight: 600, cursor: 'pointer',
          fontFamily: C.body, WebkitTapHighlightColor: 'transparent',
        }}>
          <StarSVG filled={istMarkiert} size={13} />
          {istMarkiert ? 'Gepinnt' : 'Anpinnen'}
        </button>

        <button onClick={() => onBearbeiten?.(stelle)} style={{
          flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
          padding: '9px 8px', border: `1px solid ${C.borderMid}`, borderRadius: 999,
          background: 'transparent', color: C.textMuted,
          fontSize: 12, fontWeight: 600, cursor: 'pointer',
          fontFamily: C.body, WebkitTapHighlightColor: 'transparent',
        }}>
          <EditSVG size={13} />
          Bearbeiten
        </button>
      </div>

        </div>
      </div>
    </div>
  );
}
